import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { change, Field, reduxForm } from 'redux-form'
import { Col, Row } from 'react-bootstrap'

import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Dropzone } from 'Components/Form/Dropzone'
import { LinkExternal } from 'Components/LinkExternal'
import {
  StyledConfirmButton,
  StyledCancelButton,
} from 'Components/Campaigns/Page/Conversions/CountdownTimer/styles'
import ShowReportType from 'Components/Campaigns/Page/Conversions/ShowReportType'

import { PATHS } from 'Config/Routing/paths'
import { ACTION_EDIT } from 'constants.js'
import { required } from 'Utils/Validators/formValidation'
import { getCampaignLink } from 'Utils/Link'
import { optionSortFunc } from 'Utils/Filters'
import { reportTemplateCSVFileContent } from 'Utils/Conversion'

import AlertIcon from 'Assets/Images/icons/alert'
import { CONVERSION_REPORT_TYPE } from '../../../../shared/constants'

import {
  StyledWrapper,
  StyledWarningWrapper,
  StyledDropzoneWrapper,
  StyledButtonWrapper,
} from './index'

const selectReportTypeText =
  'Please select the report type for this campaign first!'
const cannotUploadTextIntro =
  'You cannot upload a conversion report if the campaign has no setup metrics. Please '
const cannotUploadTextLink = 'edit the campaign '
const cannotUploadTextEnd = 'to add the metric you want to track.'

const AddConversionReportFormUnstyled = ({
  campaigns,
  countries,
  userCountryCode,
  form,
  className,
  changeFieldValue,
  handleSubmit,
  setSelectedCampaign,
  modalCancelHandler,
  modalConfirmHandler,
}) => {
  const history = useHistory()
  const formState = useSelector(state => state.form[form])
  const [campaignId, setCampaignId] = useState(null)
  const [campaign, setCampaign] = useState(null)
  const [countryId, setCountryId] = useState(null)
  const [campaignList, setCampaignList] = useState(null)
  const [countryList, setCountryList] = useState(null)
  const dropzoneRef = useRef(null)
  const [isCampaignHasMetric, setIsCampaignHasMetric] = useState(false)

  useEffect(() => {
    if (userCountryCode && countryList) {
      let temp = countryList.find(item => item.code === userCountryCode)
      if (!temp) {
        temp = {
          value: 1,
          label: 'Australia',
          code: 'AU',
        }
      }
      setCountryId(temp.value)
    }
  }, [userCountryCode, countryList])

  const isReportFile =
    formState && formState.values && formState.values.report ? true : false

  useEffect(() => {
    if (!campaigns || !campaigns.length || !countries || !countries.length)
      return
    if (campaignList && countryList) return
    setCampaignList(
      countries.reduce((acc, { id }) => {
        acc[id] = campaigns
          .filter(_ => _.countryId === id)
          .map(campaign => ({
            label: campaign.name,
            value: campaign.id,
          }))
          .sort(optionSortFunc)
        return acc
      }, {}),
    )
    setCountryList(
      countries
        .map(({ name, id, code }) => ({
          label: name,
          value: id,
          code: code,
        }))
        .sort(optionSortFunc),
    )
  }, [campaigns, countries])

  useEffect(() => {
    if (!countryId || !campaignList) return
    setCampaignId(null)
    changeFieldValue('country_id', countryId)
  }, [countryId, campaignList])

  useEffect(() => {
    if (!campaignId) return
    const selectedCampaign = campaigns.find(c => c.id === campaignId)
    setCampaign(selectedCampaign)

    setIsCampaignHasMetric(
      selectedCampaign &&
        selectedCampaign.metrics &&
        selectedCampaign.metrics.nodes &&
        selectedCampaign.metrics.nodes.length,
    )
    setSelectedCampaign(selectedCampaign)
    changeFieldValue('campaign_id', campaignId)
  }, [campaignId])

  const getCampaignsOptions = () => {
    if (countryId && campaignList) {
      const option = campaignList[countryId]
      return option
    } else {
      return []
    }
  }
  const getCountriesOptions = () => (countryList ? countryList : [])

  const onCampaignChange = (e, campaignId) => {
    setCampaignId(e)
  }

  const onCountryChange = (e, countryId) => {
    setCountryId(countryId)
  }

  const getReportType = () =>
    campaign &&
    campaign.conversionReportType &&
    campaign.conversionReportType !== CONVERSION_REPORT_TYPE.NO_TYPE
      ? campaign.conversionReportType
      : null

  const handleCancel = () => {
    modalCancelHandler()
  }

  const handleConfirm = () => {
    if (!getReportType()) {
      if (campaign && campaignId === campaign.id) {
        history.push(
          `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
            campaign.id
          }/conversions`,
        )
        window.location.reload(true)
        modalCancelHandler()
      }
      return
    }
    if (!isCampaignHasMetric) {
      return
    }
    modalConfirmHandler()
  }

  const handleRemoveReport = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.props.onDrop([])
    }
    changeFieldValue('report', undefined)
  }

  const renderUploadField = () => {
    const reportType = getReportType()
    if (!reportType && campaignId)
      return (
        <StyledWarningWrapper>
          <AlertIcon />
          {selectReportTypeText}
        </StyledWarningWrapper>
      )

    if (!isCampaignHasMetric && campaignId) {
      const url = getCampaignLink(campaign, ACTION_EDIT)

      return [
        cannotUploadTextIntro,
        <LinkExternal key="edit-campaign" href={url}>
          {cannotUploadTextLink}
        </LinkExternal>,
        cannotUploadTextEnd,
      ]
    }

    const metrics = campaign
      ? campaign.metrics.nodes.map(node =>
          node.label ? node.label : node.name,
        )
      : null

    return (
      <section>
        <Field
          className="upload-report"
          component={Dropzone}
          name="report"
          props={{
            accept: 'text/csv',
            innerRef: dropzoneRef,
            isReport: true,
            dropzoneLabel: 'Drop file here',
          }}
          validate={[required]}
        />
        <StyledDropzoneWrapper>
          {campaign ? (
            <ShowReportType
              reportType={reportType}
              templateCSVFile={reportTemplateCSVFileContent(
                campaign.name,
                metrics,
                reportType === CONVERSION_REPORT_TYPE.WITH_TIMESTAMP,
              )}
            />
          ) : null}
          {isReportFile ? (
            <StyledCancelButton onClick={handleRemoveReport}>
              Delete
            </StyledCancelButton>
          ) : (
            <div />
          )}
        </StyledDropzoneWrapper>
      </section>
    )
  }

  const getForm = () => {
    if (!countryId) return 'Loading'
    return (
      <form className={className} onSubmit={handleSubmit}>
        <Row>
          <Col xs={12}>
            <Field
              component={SelectDashboard}
              name="country_id"
              onChange={onCountryChange}
              props={{
                options: getCountriesOptions(),
                id: 'report_country_select',
              }}
              validate={[required]}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Field
              component={SelectDashboard}
              name="campaign_id"
              props={{
                options: getCampaignsOptions(),
                id: 'report_campaign_select',
                hasPrimaryText: true,
                primaryText: 'None',
                input: {
                  value: campaignId,
                  onChange: value => onCampaignChange(value),
                },
              }}
              validate={[required]}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>{renderUploadField()}</Col>
        </Row>
      </form>
    )
  }

  return (
    <StyledWrapper>
      {getForm()}
      <StyledButtonWrapper>
        <StyledCancelButton onClick={handleCancel}>Cancel</StyledCancelButton>
        <StyledConfirmButton
          onClick={handleConfirm}
          isReached
          disabled={!campaignId || !isCampaignHasMetric}
        >
          {!campaignId
            ? 'Select campaign'
            : getReportType()
            ? 'Confirm'
            : 'Select report type'}
        </StyledConfirmButton>
      </StyledButtonWrapper>
    </StyledWrapper>
  )
}

AddConversionReportFormUnstyled.propTypes = {
  campaigns: PropTypes.array.isRequired,
  changeFieldValue: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  initiallySelectedCampaign: PropTypes.string,
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeFieldValue: (field, value) =>
    dispatch(change(ownProps.form, field, value)),
})

export default reduxForm({
  enableReinitialize: true,
})(
  connect(
    null,
    mapDispatchToProps,
  )(AddConversionReportFormUnstyled),
)
